import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Info, X } from 'lucide-react'

export const FloatingAd = () => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [isClosed, setIsClosed] = useState(false)

  useEffect(() => {
    if (isClosed) {
      setIsVisible(false)
      return
    }

    const handleScroll = () => {
      const targetSection = document.getElementById('beneficios')
      if (targetSection) {
        const rect = targetSection.getBoundingClientRect()
        // Show when the top of the "beneficios" section is near the top of the viewport
        if (rect.top <= 100) {
          setIsVisible(true)
        } else {
          setIsVisible(false)
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isClosed])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 150, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 150, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 pointer-events-none"
        >
          <div className="max-w-250 mx-auto w-full pointer-events-auto">
            <div className="relative rounded-2xl overflow-hidden border border-white/20 shadow-[0_0_40px_rgba(82,197,110,0.12)] bg-[#0d0726]">
              {/* Background image */}
              <div
                className="absolute inset-0 z-0 bg-cover bg-center opacity-70 mix-blend-screen"
                style={{ backgroundImage: "url('/assets/ad_bg.png')" }}
              />
              {/* Overlay gradients for better text readability */}
              <div className="absolute inset-0 z-0 bg-linear-to-r from-[#170a3c] via-[#170a3c]/80 to-transparent" />
              <div className="absolute inset-0 z-0 bg-linear-to-t from-black/60 to-transparent opacity-50" />

              {/* Close Button */}
              <button
                onClick={() => setIsClosed(true)}
                className="absolute top-2 right-1.5 z-20 p-1.5 rounded-full text-white/90 hover:text-white transition-colors cursor-pointer"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Content */}
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-4 md:px-8 md:py-6">
                {/* Left Side */}
                <div className="flex items-center gap-5 w-full md:w-auto">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-3 mb-1.5">
                      <h3
                        className="text-xl md:text-2xl font-extrabold font-heading leading-none bg-clip-text text-transparent"
                        style={{
                          backgroundImage:
                            'linear-gradient(90deg, #ADC6FF 0%, #eeddff 100%)'
                        }}
                      >
                        {t('floatingAd.title')}
                      </h3>
                    </div>
                    <p className="text-white font-medium">
                      {t(
                        'floatingAd.subtitle',
                        'Claim your reward tokens now and join the next generation of decentralized finance.'
                      )}
                    </p>
                    <div className="card flex items-center gap-4 mt-3 px-3 py-1.5 rounded-lg bg-[#0d0726]/50 border border-white/10">
                      <Info className="w-4 h-4 text-red-300" />
                      <p className="text-white/70 text-sm font-medium">
                        {t(
                          'floatingAd.disclaimer',
                          'To claim, you must have an active KYC verification and an established account on OneMillionsDollar.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side */}
                <div className="flex md:flex-col items-center gap-4 w-full md:w-auto shrink-0">
                  <button
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(136, 255, 103, 0.78) 0%, #00A074 100%)'
                    }}
                    className="w-full hover:opacity-100! md:w-auto hover:shadow-[0_0_30px_rgba(136,255,103,0.3)] transition-all text-white font-bold px-6 py-3.5 rounded-xl text-sm md:text-base whitespace-nowrap"
                  >
                    {t('floatingAd.claimAirdrop', 'Claim Airdrop')}
                    <ArrowRight className="w-5 h-5 ml-2 inline-block" />
                  </button>
                  <button className="whitespace-nowrap w-full hover:opacity-100! bg-[#0d0726] border border-white/10 text-[#DAE2FD] shadow-sm rounded-xl px-6 py-3.5">
                    <p>{t('floatingAd.viewEligibility', 'View Eligibility')}</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
